// Program readiness as the server computed it: overall state, counts,
// per-department roll-up and the people table with their blockers. The
// manager view is already narrowed to reach by the API — nothing is filtered
// or re-derived here.
import { useT } from '../lib/i18n';
import React from 'react';
import type { ProgramReadiness, ReadinessState } from '@/services/learningService';
import { ReadinessBadge } from './ReadinessBadge';

const COUNT_CLS: Record<ReadinessState, string> = {
  ready: 'text-success-strong', at_risk: 'text-amber-700 dark:text-amber-300', not_ready: 'text-red-700 dark:text-red-300',
};

export const ReadinessSummary: React.FC<{ data: ProgramReadiness; className?: string }> = ({ data, className }) => {
  const t = useT();
  const counts: Array<[ReadinessState, number, string]> = [
    ['ready', data.ready, t('training:readiness_ready', 'Go-live ready')],
    ['at_risk', data.at_risk, t('training:readiness_at_risk', 'At risk')],
    ['not_ready', data.not_ready, t('training:readiness_not_ready', 'Not ready')],
  ];
  return (
    <div className={`space-y-4 ${className || ''}`} data-testid="readiness-summary">
      <div className="flex flex-wrap items-center gap-3">
        <ReadinessBadge state={data.state} size="md" />
        <span className="text-xs text-muted-foreground">{t('training:readiness_of_total', '{{n}} participant(s)', { n: data.total })}{data.computed_at ? ` · ${t('training:readiness_computed', 'computed')} ${new Date(data.computed_at).toLocaleString()}` : ''}</span>
      </div>
      <div className="grid grid-cols-3 gap-3" data-testid="readiness-counts">
        {counts.map(([state, n, label]) => (
          <div key={state} className="rounded-md border border-border p-3" data-testid={`readiness-count-${state}`}>
            <div className={`text-xl font-bold tabular-nums ${COUNT_CLS[state]}`}>{n}</div>
            <div className="text-[11px] text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
      {!data.policy?.enabled && <p className="text-xs text-amber-700 dark:text-amber-300">{t('training:readiness_no_policy', 'This program has no readiness policy — everyone counts as ready. Add one to track go-live readiness.')}</p>}

      {(data.by_department || []).length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm" data-testid="readiness-departments">
            <thead className="bg-muted text-muted-foreground text-xs">
              <tr>
                <th className="text-left font-medium px-3 py-2">{t('training:wiz_department', 'Department')}</th>
                <th className="text-right font-medium px-3 py-2">{t('training:readiness_ready', 'Go-live ready')}</th>
                <th className="text-right font-medium px-3 py-2">{t('training:readiness_at_risk', 'At risk')}</th>
                <th className="text-right font-medium px-3 py-2">{t('training:readiness_not_ready', 'Not ready')}</th>
                <th className="text-right font-medium px-3 py-2">{t('training:readiness_total', 'Total')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.by_department.map((d) => (
                <tr key={d.department || '—'}>
                  <td className="px-3 py-2 text-foreground">{d.department || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-success-strong">{d.ready}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700 dark:text-amber-300">{d.at_risk}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700 dark:text-red-300">{d.not_ready}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{d.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm" data-testid="readiness-people">
          <thead className="bg-muted text-muted-foreground text-xs">
            <tr>
              <th className="text-left font-medium px-3 py-2">{t('training:col_name', 'Name')}</th>
              <th className="text-left font-medium px-3 py-2">{t('training:wiz_department', 'Department')}</th>
              <th className="text-left font-medium px-3 py-2">{t('training:readiness', 'Readiness')}</th>
              <th className="text-left font-medium px-3 py-2">{t('training:readiness_sessions', 'Sessions')}</th>
              <th className="text-left font-medium px-3 py-2">{t('training:readiness_practices', 'Practices')}</th>
              <th className="text-left font-medium px-3 py-2">{t('training:readiness_blockers', 'Blockers')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.people || []).map((p) => (
              <tr key={p.user_id} data-testid={`readiness-person-${p.user_id}`}>
                <td className="px-3 py-2 text-foreground">{p.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{p.department || '—'}</td>
                <td className="px-3 py-2"><ReadinessBadge state={p.state} /></td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums whitespace-nowrap">
                  {t('training:readiness_attended_of', '{{a}}/{{r}} attended', { a: p.attended_sessions, r: p.required_sessions })}
                  {p.critical_total > 0 ? ` · ${t('training:readiness_critical_of', '{{d}}/{{n}} critical', { d: p.critical_done, n: p.critical_total })}` : ''}
                  {p.missed_sessions > 0 ? ` · ${t('training:readiness_missed_n', '{{n}} missed', { n: p.missed_sessions })}` : ''}
                  {p.upcoming_sessions > 0 ? ` · ${t('training:readiness_upcoming_n', '{{n}} upcoming', { n: p.upcoming_sessions })}` : ''}
                </td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums whitespace-nowrap">
                  {p.practices_total > 0 ? t('training:readiness_validated_of', '{{v}}/{{n}} validated', { v: p.practices_validated, n: p.practices_total }) : '—'}
                  {p.verification_pending > 0 ? ` · ${t('training:readiness_pending_n', '{{n}} pending verification', { n: p.verification_pending })}` : ''}
                </td>
                <td className="px-3 py-2">
                  {(p.blockers || []).length === 0 ? <span className="text-muted-foreground">—</span> : (
                    <ul className="list-disc pl-4 text-xs text-foreground space-y-0.5">{p.blockers.map((b, i) => <li key={`${i}-${b}`}>{b}</li>)}</ul>
                  )}
                </td>
              </tr>
            ))}
            {(data.people || []).length === 0 && (
              <tr><td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">{t('training:no_participants', 'No participants yet.')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReadinessSummary;
